import {
  ApiError,
  LoginResponse,
  StorageAdapter,
  SessionAdapter,
  LoggerAdapter,
} from "./types";

/**
 * Timeout constants for API client operations
 */
const TIMEOUTS = {
  SESSION_CHECK: 2000,
  SESSION_RETRY: 5000,
  REQUEST: 30000,
  AUTH_REQUEST: 10000, // Shorter timeout for auth endpoints to prevent login delays
} as const;

/**
 * Maximum length of response preview in error messages
 */
const MAX_ERROR_PREVIEW_LENGTH = 500;

/** Status of an asynchronous AI profile-evaluation job. */
export type ProfileEvaluationJobStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed";

/**
 * Response shape for both starting a profile evaluation and polling its status.
 * Result fields are populated once `status` is `completed`; `error` is set when
 * `status` is `failed`.
 */
export interface ProfileEvaluationStatusResponse {
  job_id: string;
  status: ProfileEvaluationJobStatus;
  suggested_profile: string | null;
  summary: string | null;
  observations: string[];
  analyzed_count: number;
  error: string | null;
  request_id?: string;
}

/**
 * Safely extracts an error message from an unknown error value.
 * Handles Error instances, objects with message or detail property, and primitive values.
 *
 * @param error - The error value (can be Error, object, string, etc.)
 * @returns A string representation of the error message
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message || error.name || "Unknown error";
  }
  if (error && typeof error === "object") {
    // Check for 'message' property first (most common)
    if ("message" in error && error.message != null) {
      return String(error.message);
    }
    // Check for 'detail' property (common in API error responses)
    if ("detail" in error && error.detail != null) {
      return String(error.detail);
    }
  }
  if (typeof error === "string") {
    return error;
  }
  return String(error) || "Unknown error";
}

/**
 * Determines if an error is a network-level error (Failed to fetch, CORS, etc.)
 *
 * Network errors can occur due to:
 * - Network connectivity issues
 * - CORS errors
 * - SSL/TLS certificate issues
 * - Server not responding
 * - Request being blocked by browser security
 *
 * @param error - Error to check
 * @returns True if error is a network error
 */
function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    message === "failed to fetch" ||
    message.includes("network") ||
    message.includes("cors") ||
    message.includes("fetch")
  );
}

export class ApiClient {
  private baseUrl: string;
  private storage: StorageAdapter;
  private session?: SessionAdapter;
  private logger?: LoggerAdapter;
  private sessionCheckCache: {
    token: string | null;
    timestamp: number;
  } | null = null;
  private readonly SESSION_CACHE_MS = 1000;
  // Lock to prevent multiple simultaneous 401 retries from clearing token
  private refreshLock: Promise<void> | null = null;

  constructor(options: {
    baseUrl?: string;
    storage: StorageAdapter;
    session?: SessionAdapter;
    logger?: LoggerAdapter;
  }) {
    this.baseUrl = options.baseUrl || "";
    this.storage = options.storage;
    this.session = options.session;
    this.logger = options.logger;
  }

  /**
   * Proactively refreshes the session token from the web app.
   * Call this when the user returns after being idle to prevent 401 errors.
   * Fetches the fresh token BEFORE touching storage: if the web app tab fetch
   * fails or times out (a realistic idle-return scenario — the tab may not be
   * open, active, or have its content script loaded yet), the currently
   * stored token is left in place. That old token may still be valid, so
   * clearing it pre-emptively would cause a spurious logout in exactly the
   * case this proactive refresh is meant to prevent.
   */
  async refreshSession(): Promise<void> {
    if (!this.session) {
      return;
    }

    // Clear the session cache so we don't reuse a stale cached-token check.
    this.sessionCheckCache = null;

    // Fetch fresh token from web app
    const sessionResult = await this.session.getSession(TIMEOUTS.SESSION_CHECK);
    const session = sessionResult.data?.session;

    if (session?.access_token) {
      await this.storage.setToken(session.access_token);
      this.logger?.debug("Session refreshed successfully");
    } else {
      this.logger?.debug(
        "No session available during refresh; keeping existing token",
      );
    }
  }

  /**
   * Attempts to refresh the session and get a new access token
   * @param storedToken - The currently stored token (may be null)
   * @returns The new access token if refresh succeeds, or the stored token if refresh fails
   */
  /**
   * Refreshes session from web app tab if needed.
   * Only attempts refresh if forceRefresh is true or no stored token exists.
   * This avoids unnecessary tab lookups when we have a valid stored token.
   */
  private async refreshSessionIfNeeded(
    storedToken: string | null,
    forceRefresh: boolean = false,
  ): Promise<string | null> {
    if (!this.session) {
      return storedToken;
    }

    // If we have a stored token and not forcing refresh, use it directly
    // This avoids unnecessary web app tab lookups on every API call
    if (storedToken && !forceRefresh) {
      return storedToken;
    }

    // Check cache first to avoid duplicate session checks in rapid succession
    const now = Date.now();
    if (
      this.sessionCheckCache &&
      now - this.sessionCheckCache.timestamp < this.SESSION_CACHE_MS
    ) {
      return this.sessionCheckCache.token;
    }

    // Only try to refresh from web app tab if:
    // - No stored token (first login)
    // - Force refresh (token expired, 401 error)
    try {
      const sessionResult = await this.session.getSession(
        TIMEOUTS.SESSION_CHECK,
      );
      const session = sessionResult.data?.session;

      if (session?.access_token) {
        await this.storage.setToken(session.access_token);
        // Update cache
        this.sessionCheckCache = {
          token: session.access_token,
          timestamp: now,
        };
        return session.access_token;
      }

      if (sessionResult.error) {
        this.logger?.warn("Failed to check session, using stored token", {
          error: sessionResult.error.message,
        });
      }

      // Update cache with stored token (even if refresh failed)
      this.sessionCheckCache = {
        token: storedToken,
        timestamp: now,
      };
    } catch (error) {
      this.logger?.warn("Error checking session", { error });
      // Update cache with stored token on error
      this.sessionCheckCache = {
        token: storedToken,
        timestamp: now,
      };
    }

    return storedToken;
  }

  private async getToken(): Promise<string | null> {
    const storedToken = await this.storage.getToken();

    // Use stored token directly - no need to check web app tab on every call
    // This allows the extension to work without tabs permission for normal operation
    if (storedToken) {
      return storedToken;
    }

    // Only try to get from web app tab if no stored token (first login)
    if (this.session) {
      return await this.refreshSessionIfNeeded(null, true);
    }

    return null;
  }

  /**
   * Checks if response text looks like HTML (common in redirects or error pages).
   * This helps detect stale window scenarios where expired tokens cause HTML redirects.
   *
   * @param responseText - The response text to check for HTML indicators
   * @returns True if the response appears to be HTML, false otherwise
   */
  private isHtmlResponse(responseText: string): boolean {
    const trimmed = responseText.trim();
    if (!trimmed) return false;

    // Check for common HTML/XML document indicators
    const htmlIndicators = [
      /^<!DOCTYPE\s+html/i,
      /^<html[\s>]/i,
      /^<\?xml/i, // XML declaration
      /^<!--/, // HTML comment
    ];

    if (htmlIndicators.some((pattern) => pattern.test(trimmed))) {
      return true;
    }

    // Check for HTML structure (opening and closing tags)
    const hasHtmlStructure =
      (trimmed.includes("<html") && trimmed.includes("</html>")) ||
      (trimmed.includes("<body") && trimmed.includes("</body>")) ||
      (trimmed.includes("<head") && trimmed.includes("</head>"));

    return hasHtmlStructure;
  }

  /**
   * Looser, deliberately-different sibling of {@link isHtmlResponse}, used ONLY as a
   * fallback inside {@link buildJsonParseErrorMessage} for wording a better error message
   * after `isHtmlResponse` has ALREADY run (at the JSON.parse call site) and returned false.
   *
   * `isHtmlResponse` requires either an anchored document-level indicator (`<!DOCTYPE html`,
   * `<html>`, etc.) or a matched pair of opening+closing tags - it deliberately avoids
   * flagging plain text that merely mentions HTML. This check exists to catch responses
   * `isHtmlResponse` would miss precisely because of that stricter closing-tag requirement -
   * e.g. a TRUNCATED or streamed HTML error page that starts with a doctype/`<html` but was
   * cut off before its closing tag. Do not replace this with a call to `isHtmlResponse`: that
   * would make this fallback check redundant with the one that already ran and failed to
   * catch the response, defeating its purpose.
   */
  private looksLikePartialOrUnclosedHtml(responseText: string): boolean {
    return (
      responseText.trim().startsWith("<") &&
      (responseText.includes("<html") || responseText.includes("<!DOCTYPE"))
    );
  }

  /**
   * Safely parse JSON from a Response, providing detailed error messages on failure.
   * Logs errors for observability and includes response preview in error message.
   *
   * @param response - Fetch Response object
   * @param context - Optional context string for error messages (e.g., "after retry")
   * @returns Parsed JSON data
   * @throws Error with detailed message if JSON parsing fails
   *
   * @example
   * ```typescript
   * const response = await fetch('/api/data');
   * const data = await this.parseJsonResponse<DataResponse>(response);
   * // If JSON parsing fails, throws Error with response preview
   * ```
   */

  private async parseJsonResponse<T>(
    response: Response,
    context: string = "",
  ): Promise<T> {
    // Maximum response body size (10MB) to prevent memory issues
    const MAX_RESPONSE_SIZE = 10 * 1024 * 1024;
    const contentLength = response.headers.get("content-length");
    if (contentLength) {
      const size = parseInt(contentLength, 10);
      if (!isNaN(size) && size > MAX_RESPONSE_SIZE) {
        throw new Error(
          `Response body too large (${size} bytes, max ${MAX_RESPONSE_SIZE} bytes)`,
        );
      }
    }

    // Read response as text first so we can always access the body for error messages
    // If we call response.json() first and it fails, the body stream is consumed
    // and we can't read it again with response.text()
    const responseText = await response
      .text()
      .catch(() => "Unable to read response");

    // If response is empty, return empty object (for cases like 204 No Content)
    if (!responseText || responseText.trim() === "") {
      return {} as T;
    }

    // Detect HTML responses (common in stale window scenarios with expired tokens)
    // This prevents trying to parse HTML as JSON, which throws "Invalid data"
    if (this.isHtmlResponse(responseText)) {
      const contextMsg = context ? ` ${context}` : "";
      const isAuthError = response.status === 401 || response.status === 403;
      const errorHint = isAuthError
        ? " This may indicate an expired session. Please refresh the page."
        : "";
      const contentType =
        response.headers.get("content-type") || "unknown content type";

      this.logger?.error("Received HTML response instead of JSON", {
        context: contextMsg.trim() || "main request",
        status: response.status,
        statusText: response.statusText,
        contentType,
        responsePreview: responseText.substring(0, MAX_ERROR_PREVIEW_LENGTH),
        isAuthError,
      });

      throw new Error(
        `Expected JSON response but received HTML (content-type: ${contentType})${contextMsg}. Status: ${response.status} ${response.statusText}.${errorHint}`,
      );
    }

    try {
      return JSON.parse(responseText) as T;
    } catch (jsonError) {
      // Extract error message - handle cases where error might not be an Error instance
      let jsonErrorMessage: string;
      if (jsonError instanceof Error) {
        jsonErrorMessage =
          jsonError.message || jsonError.name || "Unknown JSON parse error";
      } else if (typeof jsonError === "string") {
        jsonErrorMessage = jsonError;
      } else {
        jsonErrorMessage = String(jsonError) || "Unknown JSON parse error";
      }

      const contextMsg = context ? ` ${context}` : "";

      // Log error for observability
      this.logger?.error("Failed to parse JSON response", {
        context: contextMsg.trim() || "main request",
        error: jsonErrorMessage,
        responsePreview: responseText.substring(0, MAX_ERROR_PREVIEW_LENGTH),
        status: response.status,
        statusText: response.statusText,
        responseLength: responseText.length,
        // Include original error type for debugging
        errorType:
          jsonError instanceof Error
            ? jsonError.constructor.name
            : typeof jsonError,
      });

      // Build descriptive error message
      const errorMessage = this.buildJsonParseErrorMessage(
        jsonErrorMessage,
        responseText,
        response,
        contextMsg,
      );

      // Always throw an Error instance (not a string) to ensure proper error handling
      // Include the original error as cause if available (for better stack traces)
      const enhancedError = new Error(errorMessage);
      if (jsonError instanceof Error && "cause" in Error.prototype) {
        (enhancedError as any).cause = jsonError;
      }
      throw enhancedError;
    }
  }

  /**
   * Builds a descriptive error message for JSON parse failures.
   * Provides context about response type, status, and preview.
   *
   * @param jsonErrorMessage - Original error message from JSON.parse
   * @param responseText - Response body text that failed to parse
   * @param response - Response object with status and headers
   * @param contextMsg - Optional context string (e.g., "after retry")
   * @returns Descriptive error message
   */
  private buildJsonParseErrorMessage(
    jsonErrorMessage: string,
    responseText: string,
    response: Response,
    contextMsg: string,
  ): string {
    // Handle various JSON parse error messages including "Invalid data", "Unexpected token", etc.
    const isGenericError =
      jsonErrorMessage === "Invalid data" ||
      jsonErrorMessage.includes("JSON") ||
      jsonErrorMessage.includes("Unexpected") ||
      jsonErrorMessage === "SyntaxError";

    // Check if response looks like HTML even though isHtmlResponse didn't catch it
    // (see looksLikePartialOrUnclosedHtml's doc comment for why this is a deliberately
    // separate, looser check rather than a call to isHtmlResponse).
    const looksLikeHtml = this.looksLikePartialOrUnclosedHtml(responseText);

    if (looksLikeHtml) {
      const isAuthError = response.status === 401 || response.status === 403;
      const errorHint = isAuthError
        ? " This may indicate an expired session. Please refresh the page."
        : "";
      return `Expected JSON response but received HTML${contextMsg}. Status: ${response.status} ${response.statusText}.${errorHint} Response preview: ${responseText.substring(0, MAX_ERROR_PREVIEW_LENGTH)}`;
    } else if (isGenericError) {
      // Provide more context for generic "Invalid data" errors
      const responseType =
        responseText.trim().length === 0
          ? "empty response"
          : responseText.trim().length < 50
            ? `short response (${responseText.trim().length} chars)`
            : "malformed JSON";

      // For "Invalid data" specifically, provide additional context
      const additionalContext =
        jsonErrorMessage === "Invalid data"
          ? " The server may have returned HTML, plain text, or corrupted JSON instead of valid JSON."
          : "";

      const isAuthError = response.status === 401 || response.status === 403;
      const authHint = isAuthError
        ? " This may indicate an expired session. Please refresh the page."
        : "";

      return `Invalid JSON in response${contextMsg} (${responseType}).${additionalContext} Status: ${response.status} ${response.statusText}.${authHint} Response preview: ${responseText.substring(0, MAX_ERROR_PREVIEW_LENGTH)}`;
    } else {
      return `Failed to parse JSON response${contextMsg}: ${jsonErrorMessage}. Status: ${response.status} ${response.statusText}. Response preview: ${responseText.substring(0, MAX_ERROR_PREVIEW_LENGTH)}`;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const needsAuth =
      !endpoint.includes("/api/auth/login") &&
      !endpoint.includes("/api/auth/register");
    const isAuthEndpoint = endpoint.includes("/api/auth/");
    const token = needsAuth ? await this.getToken() : null;
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    // Use shorter timeout for auth endpoints to prevent login delays
    const timeout = isAuthEndpoint ? TIMEOUTS.AUTH_REQUEST : TIMEOUTS.REQUEST;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const fetchOptions: RequestInit = {
        ...options,
        headers: {
          ...headers,
          "Cache-Control": "no-cache",
        },
        signal: controller.signal,
        cache: "no-store",
      };

      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        let error: ApiError;
        try {
          // Use text-first pattern for consistency and better error messages
          // This ensures we can always access the response body even if JSON parsing fails
          const responseText = await response
            .text()
            .catch(() => "Unable to read response");
          if (responseText && responseText.trim()) {
            try {
              const errorData = JSON.parse(responseText);
              error = {
                detail:
                  errorData?.detail ||
                  errorData?.message ||
                  response.statusText ||
                  "Request failed",
              };
            } catch {
              // Invalid JSON in error response - use raw text if it looks like an error message
              // Limit to 200 chars to avoid huge error messages
              error = {
                detail:
                  responseText.length < 200
                    ? responseText
                    : response.statusText || "Request failed",
              };
            }
          } else {
            error = { detail: response.statusText || "Request failed" };
          }
        } catch {
          error = { detail: response.statusText || "Request failed" };
        }

        if (response.status === 401 && needsAuth && this.session) {
          // Use a lock to prevent multiple parallel 401 retries from clearing the token
          // This prevents race conditions when multiple requests fail simultaneously
          if (!this.refreshLock) {
            this.refreshLock = this.handle401Retry(url, options, endpoint);
          }

          // Wait for the refresh to complete (or fail)
          await this.refreshLock;

          // After refresh attempt, retry the request with new token
          const newToken = await this.getToken();
          if (newToken) {
            const retryHeaders: Record<string, string> = {
              "Content-Type": "application/json",
              ...(options.headers as Record<string, string>),
              Authorization: `Bearer ${newToken}`,
            };

            const retryResponse = await fetch(url, {
              ...options,
              headers: retryHeaders,
            });

            if (retryResponse.ok) {
              const contentType = retryResponse.headers.get("content-type");
              if (!contentType || !contentType.includes("application/json")) {
                return {} as T;
              }
              return await this.parseJsonResponse<T>(
                retryResponse,
                "after retry",
              );
            }
          }

          // If retry failed, clear the lock and throw error
          this.refreshLock = null;
        }

        // For 401 errors, include status code in error message for better detection
        const errorDetail = error.detail || "Request failed";
        const errorWithStatus =
          response.status === 401
            ? `Unauthorized: ${errorDetail}`
            : errorDetail;
        throw new Error(errorWithStatus);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        // If we expected JSON but got something else, read the response to check if it's HTML
        // This helps detect stale window scenarios where expired tokens cause HTML redirects
        const responseText = await response
          .text()
          .catch(() => "Unable to read response");
        const isHtml = this.isHtmlResponse(responseText);
        const isAuthError = response.status === 401 || response.status === 403;
        const errorHint =
          isHtml && isAuthError
            ? " This may indicate an expired session. Please refresh the page."
            : "";
        const contentTypeMsg = contentType || "unknown content type";

        this.logger?.error("Non-JSON response received", {
          endpoint,
          contentType: contentTypeMsg,
          status: response.status,
          statusText: response.statusText,
          isHtml,
          isAuthError,
          responsePreview: responseText.substring(0, 200),
        });

        const responsePreview = responseText.substring(0, 200);
        const htmlNote = isHtml ? " (HTML detected)" : "";
        throw new Error(
          `Expected JSON response but received ${contentTypeMsg}${htmlNote}. Status: ${response.status} ${response.statusText}.${errorHint} Response: ${responsePreview}`,
        );
      }

      return await this.parseJsonResponse<T>(response);
    } catch (error: unknown) {
      clearTimeout(timeoutId);

      // Handle timeout errors
      if (error instanceof Error && error.name === "AbortError") {
        const timeoutUsed = isAuthEndpoint
          ? TIMEOUTS.AUTH_REQUEST
          : TIMEOUTS.REQUEST;
        this.logger?.error("Request timed out", {
          endpoint,
          timeout: timeoutUsed,
        });
        throw new Error(
          "Request timed out. Please check your connection and try again.",
        );
      }

      // Handle network errors (Failed to fetch, CORS, etc.)
      if (isNetworkError(error) && error instanceof TypeError) {
        this.handleNetworkError(error, endpoint, url);
      }

      // Extract meaningful error message and create a proper Error instance
      // This ensures consistent error handling for network errors, timeouts, and other fetch failures
      const errorMessage = extractErrorMessage(error);

      // 401 Unauthorized errors are expected for unauthenticated users (e.g., on login page)
      // Log them at debug level to avoid flooding error tracking with expected auth failures
      const isUnauthorizedError =
        errorMessage.toLowerCase().includes("unauthorized") ||
        errorMessage.includes("401") ||
        (error instanceof Error &&
          error.message?.toLowerCase().includes("unauthorized"));

      if (isUnauthorizedError) {
        this.logger?.debug(
          "Unauthorized request (expected for unauthenticated users)",
          {
            endpoint,
            url,
            error: errorMessage,
          },
        );
      } else {
        this.logger?.error("Request failed", {
          endpoint,
          url,
          error: errorMessage,
          errorName: error instanceof Error ? error.name : "Unknown",
        });
      }

      throw new Error(
        errorMessage ||
          "Request failed. Please check your connection and try again.",
      );
    }
  }

  /**
   * Handles 401 retry logic with locking to prevent race conditions.
   * Only one refresh attempt runs at a time, even if multiple requests fail simultaneously.
   *
   * @param url - Full URL to retry
   * @param options - Original request options
   * @param endpoint - API endpoint for logging
   * @returns Promise that resolves when refresh attempt completes
   */
  private async handle401Retry(
    url: string,
    options: RequestInit,
    endpoint: string,
  ): Promise<void> {
    try {
      const sessionResult = await this.session!.getSession(
        TIMEOUTS.SESSION_RETRY,
      );
      const session = sessionResult.data?.session;

      if (session?.access_token) {
        await this.storage.setToken(session.access_token);
        this.logger?.debug("Token refreshed after 401", { endpoint });
        return;
      }

      // Session refresh failed - sign out and clear token
      // This is expected when user is not authenticated, so don't log as error
      this.logger?.debug("Session refresh failed after 401", { endpoint });
      await this.session!.signOut();
      await this.storage.removeToken();
    } catch (refreshError) {
      // If refresh fails, clear token but don't throw - let the original error propagate
      this.logger?.warn("Error during 401 retry", {
        endpoint,
        error:
          refreshError instanceof Error
            ? refreshError.message
            : String(refreshError),
      });
      await this.storage.removeToken();
    } finally {
      // Clear the lock after a short delay to allow other requests to see the new token
      // This prevents immediate re-locking if another request is waiting
      setTimeout(() => {
        this.refreshLock = null;
      }, 100);
    }
  }

  /**
   * Handles network-level errors (Failed to fetch, CORS, etc.)
   *
   * Network errors can occur due to:
   * - Network connectivity issues
   * - CORS errors
   * - SSL/TLS certificate issues
   * - Server not responding
   * - Request being blocked by browser security
   *
   * @param error - The network error to handle (must be a TypeError)
   * @param endpoint - API endpoint that failed
   * @param url - Full URL that failed
   * @throws Error with user-friendly message
   */
  private handleNetworkError(
    error: TypeError,
    endpoint: string,
    url: string,
  ): never {
    const logContext = this.buildErrorContext(error, endpoint, url);
    // Log network errors at warn level since they're often transient
    // This reduces noise in error tracking while still capturing the issue
    this.logger?.warn("Network request failed", logContext);

    // Standardized network error message
    const NETWORK_ERROR_MESSAGE =
      "Network request failed. Please check your internet connection and try again. If the problem persists, the server may be temporarily unavailable.";

    throw new Error(NETWORK_ERROR_MESSAGE);
  }

  /**
   * Builds error context object for logging.
   *
   * @param error - The error to build context for
   * @param endpoint - API endpoint that failed
   * @param url - Full URL that failed
   * @returns Error context object with endpoint, URL, error message, and optional cause
   */
  private buildErrorContext(
    error: Error,
    endpoint: string,
    url: string,
  ): Record<string, unknown> {
    const context: Record<string, unknown> = {
      endpoint,
      url,
      error: error.message,
      errorName: error.name,
    };
    // Include additional context if available
    if ("cause" in error && error.cause) {
      context.cause = String(error.cause);
    }
    return context;
  }

  // Auth methods
  /**
   * Register a new user account.
   * @param email - User email address
   * @param password - User password
   * @returns Promise resolving to registration response
   */
  async register(email: string, password: string) {
    return this.request<any>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  /**
   * Login with email and password.
   * @param email - User email address
   * @param password - User password
   * @returns Promise resolving to LoginResponse with access token
   */
  async login(email: string, password: string) {
    return this.request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  /**
   * Get current authenticated user information.
   * Always bypasses any HTTP cache via a cache-busting query param.
   * @returns Promise resolving to user object with subscription and Gmail account info
   */
  async getCurrentUser() {
    const endpoint = "/api/auth/me?_=" + Date.now();
    return this.request<any>(endpoint);
  }

  /**
   * Check if user is currently authenticated.
   * @returns Promise resolving to true if valid token exists, false otherwise
   */
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    return token !== null;
  }

  // Gmail OAuth
  /**
   * Initiate Google OAuth flow for general authentication.
   * @param gmailAccountId - Optional Gmail account ID for reconnect flows
   * @returns Promise resolving to authorization URL for redirect
   */
  async initiateGoogleOAuth(gmailAccountId?: string) {
    const url = gmailAccountId
      ? `/api/auth/google?gmail_account_id=${encodeURIComponent(gmailAccountId)}`
      : "/api/auth/google";
    return this.request<{ authorization_url: string }>(url);
  }

  /**
   * Initiate Google OAuth flow for signup with optional referral code.
   * @param referralCode - Optional referral code from URL parameter
   * @returns Promise resolving to authorization URL for redirect
   */
  async initiateGoogleOAuthSignup(referralCode?: string | null) {
    const url = referralCode
      ? `/api/auth/google/signup?ref=${encodeURIComponent(referralCode)}`
      : "/api/auth/google/signup";
    return this.request<{ authorization_url: string }>(url);
  }

  /**
   * Initiate Google OAuth flow for login.
   * @returns Promise resolving to authorization URL for redirect
   */
  async initiateGoogleOAuthLogin() {
    return this.request<{ authorization_url: string }>(
      "/api/auth/google/login",
    );
  }

  /**
   * Disconnect a Gmail account from the user's profile.
   * @param accountId - Optional Gmail account ID. If not provided, disconnects all accounts.
   * @returns Promise resolving to disconnect result
   */
  async disconnectGmail(accountId?: string) {
    const url = accountId
      ? `/api/gmail/accounts/${encodeURIComponent(accountId)}`
      : "/api/auth/google/disconnect";
    return this.request<any>(url, {
      method: "DELETE",
    });
  }

  /**
   * Get all connected Gmail accounts for the current user.
   * @returns Promise resolving to array of Gmail account objects
   */
  async getGmailAccounts() {
    return this.request<{ accounts: any[] }>("/api/gmail/accounts");
  }

  /**
   * Set a Gmail account as the primary account.
   * @param accountId - Gmail account ID to set as primary
   * @returns Promise resolving to updated account status
   */
  async setPrimaryGmailAccount(accountId: string) {
    return this.request<any>(
      `/api/gmail/accounts/${encodeURIComponent(accountId)}/primary`,
      {
        method: "PUT",
      },
    );
  }

  // User settings
  /**
   * Get user settings for a specific Gmail account.
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account.
   * @returns Promise resolving to user settings (threshold, action_preference, label_name)
   */
  async getUserSettings(gmailAccountId?: string) {
    const url = gmailAccountId
      ? `/api/user/settings?gmail_account_id=${encodeURIComponent(gmailAccountId)}`
      : "/api/user/settings";
    return this.request<any>(url);
  }

  /**
   * Update user settings for a specific Gmail account.
   * @param settings - Settings to update (threshold, action_preference, label_name, gmail_account_id)
   * @returns Promise resolving to updated user settings
   */
  async updateUserSettings(settings: {
    threshold?: number;
    action_preference?: string;
    label_name?: string;
    gmail_account_id?: string;
    auto_allowlist_on_label_removal?: boolean;
    auto_blocklist_on_label_addition?: boolean;
    auto_create_tasks_for_important?: boolean;
    task_creation_sensitivity?: "low" | "medium" | "high";
    user_profile?: string;
  }) {
    return this.request<any>("/api/user/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  }

  /**
   * Run an AI profile evaluation and get the suggested email profile. The work
   * runs server-side and the suggestion is returned for review (status
   * `completed` with the profile, or `failed` with a message). It is not saved
   * automatically.
   * @param gmailAccountId - Optional Gmail account ID. Defaults to primary account.
   */
  async startProfileEvaluation(gmailAccountId?: string) {
    return this.request<ProfileEvaluationStatusResponse>(
      "/api/user/profile/evaluate",
      {
        method: "POST",
        body: JSON.stringify(
          gmailAccountId ? { gmail_account_id: gmailAccountId } : {},
        ),
      },
    );
  }

  /**
   * Check if user has Google Tasks scope for a Gmail account.
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account
   * @returns Promise resolving to object with hasScope, gmailEmail, and gmailAccountId
   */
  async checkTasksScope(gmailAccountId?: string) {
    const url = gmailAccountId
      ? `/api/user/tasks-scope?gmail_account_id=${encodeURIComponent(gmailAccountId)}`
      : "/api/user/tasks-scope";
    return this.request<{
      hasScope: boolean;
      gmailEmail: string;
      gmailAccountId: string;
    }>(url);
  }

  // Folder management
  /**
   * Get folder settings for a specific Gmail account.
   * @param gmailAccountId - Gmail account ID to get folder settings for
   * @returns Promise resolving to folder settings with enabled folders and custom folders
   */
  async getFolderSettings(gmailAccountId: string) {
    return this.request<any>(
      `/api/user/folders?gmail_account_id=${encodeURIComponent(gmailAccountId)}`,
    );
  }

  /**
   * Update folder settings for a specific Gmail account.
   * @param gmailAccountId - Gmail account ID to update folder settings for
   * @param enabledFolders - Array of enabled folders with configuration
   * @returns Promise resolving to updated folder settings
   */
  async updateFolderSettings(
    gmailAccountId: string,
    enabledFolders: Array<{
      type: "standard" | "custom";
      id: string;
      markAsRead?: boolean;
      keepInInbox?: boolean;
      userContext?: string;
    }>,
  ) {
    return this.request<any>("/api/user/folders", {
      method: "PUT",
      body: JSON.stringify({
        gmail_account_id: gmailAccountId,
        enabled_folders: enabledFolders,
      }),
    });
  }

  // Custom folder management
  /**
   * Get all custom folders for a specific Gmail account.
   * @param gmailAccountId - Gmail account ID to get custom folders for
   * @returns Promise resolving to array of custom folder objects
   */
  async getCustomFolders(gmailAccountId: string) {
    return this.request<any>(
      `/api/user/custom-folders?gmail_account_id=${encodeURIComponent(gmailAccountId)}`,
    );
  }

  /**
   * Create a new custom folder for a Gmail account.
   * @param gmailAccountId - Gmail account ID to create folder for
   * @param name - Folder name
   * @param description - Optional folder description
   * @param order - Optional display order
   * @param routingMode - Routing mode: 'strict' for rule-based, 'ai' for LLM-based (default: 'ai')
   * @param rules - Optional structured routing rules (used when routingMode is 'strict')
   * @returns Promise resolving to created custom folder
   */
  async createCustomFolder(
    gmailAccountId: string,
    name: string,
    description?: string | null,
    order?: number,
    routingMode?: "strict" | "ai",
    rules?: {
      sender?: {
        conditions: Array<{
          type: "contains" | "domain" | "email";
          value: string;
        }>;
        match: "any" | "all";
      };
      subject?: {
        conditions: Array<{
          type: "contains" | "starts_with" | "ends_with";
          value: string;
        }>;
        match: "any" | "all";
      };
      combine: "and" | "or";
    } | null,
  ) {
    return this.request<any>("/api/user/custom-folders", {
      method: "POST",
      body: JSON.stringify({
        gmail_account_id: gmailAccountId,
        name,
        description: description || null,
        order,
        routing_mode: routingMode,
        rules,
      }),
    });
  }

  /**
   * Update an existing custom folder.
   * @param id - Custom folder ID to update
   * @param gmailAccountId - Gmail account ID that owns the folder
   * @param updates - Fields to update (name, description, order, routing_mode, rules)
   * @returns Promise resolving to updated custom folder
   */
  async updateCustomFolder(
    id: string,
    gmailAccountId: string,
    updates: {
      name?: string;
      description?: string | null;
      order?: number;
      routing_mode?: "strict" | "ai";
      rules?: {
        sender?: {
          conditions: Array<{
            type: "contains" | "domain" | "email";
            value: string;
          }>;
          match: "any" | "all";
        };
        subject?: {
          conditions: Array<{
            type: "contains" | "starts_with" | "ends_with";
            value: string;
          }>;
          match: "any" | "all";
        };
        combine: "and" | "or";
      } | null;
    },
  ) {
    return this.request<any>("/api/user/custom-folders", {
      method: "PUT",
      body: JSON.stringify({
        id,
        gmail_account_id: gmailAccountId,
        ...updates,
      }),
    });
  }

  /**
   * Delete a custom folder.
   * @param id - Custom folder ID to delete
   * @param gmailAccountId - Gmail account ID that owns the folder
   * @returns Promise resolving to deletion result
   */
  async deleteCustomFolder(id: string, gmailAccountId: string) {
    return this.request<any>("/api/user/custom-folders", {
      method: "DELETE",
      body: JSON.stringify({
        id,
        gmail_account_id: gmailAccountId,
      }),
    });
  }

  // Allowlist
  /**
   * Get allowlist entries for a specific Gmail account.
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account.
   * @returns Promise resolving to array of allowlist entry objects
   */
  async getAllowlist(gmailAccountId?: string, limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (gmailAccountId) {
      params.append("gmail_account_id", gmailAccountId);
    }
    if (limit !== undefined) {
      params.append("limit", String(limit));
    }
    if (offset !== undefined) {
      params.append("offset", String(offset));
    }
    const queryString = params.toString();
    const url = queryString
      ? `/api/user/allowlist?${queryString}`
      : "/api/user/allowlist";
    return this.request<
      any[] | { entries: any[]; total: number; limit: number; offset: number }
    >(url);
  }

  /**
   * Add a new allowlist entry (pattern that always passes spam check).
   * @param pattern - Email pattern to allowlist (supports wildcards like *@example.com)
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account.
   * @returns Promise resolving to created allowlist entry
   */
  async addAllowlistEntry(pattern: string, gmailAccountId?: string) {
    return this.request<any>("/api/user/allowlist", {
      method: "POST",
      body: JSON.stringify({ pattern, gmail_account_id: gmailAccountId }),
    });
  }

  /**
   * Delete an allowlist entry.
   * @param entryId - Allowlist entry ID to delete
   * @returns Promise resolving to deletion result
   */
  async deleteAllowlistEntry(entryId: string) {
    return this.request<any>(`/api/user/allowlist/${entryId}`, {
      method: "DELETE",
    });
  }

  // Blocklist
  /**
   * Get blocklist entries for a specific Gmail account.
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account.
   * @returns Promise resolving to array of blocklist entry objects
   */
  async getBlocklist(gmailAccountId?: string, limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (gmailAccountId) {
      params.append("gmail_account_id", gmailAccountId);
    }
    if (limit !== undefined) {
      params.append("limit", String(limit));
    }
    if (offset !== undefined) {
      params.append("offset", String(offset));
    }
    const queryString = params.toString();
    const url = queryString
      ? `/api/user/blocklist?${queryString}`
      : "/api/user/blocklist";
    return this.request<
      any[] | { entries: any[]; total: number; limit: number; offset: number }
    >(url);
  }

  /**
   * Add a new blocklist entry (pattern that always fails spam check).
   * @param pattern - Email pattern to blocklist (supports wildcards like *@example.com)
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account.
   * @returns Promise resolving to created blocklist entry
   */
  async addBlocklistEntry(pattern: string, gmailAccountId?: string) {
    return this.request<any>("/api/user/blocklist", {
      method: "POST",
      body: JSON.stringify({ pattern, gmail_account_id: gmailAccountId }),
    });
  }

  /**
   * Delete a blocklist entry.
   * @param entryId - Blocklist entry ID to delete
   * @returns Promise resolving to deletion result
   */
  async deleteBlocklistEntry(entryId: string) {
    return this.request<any>(`/api/user/blocklist/${entryId}`, {
      method: "DELETE",
    });
  }

  // Routing rules
  /**
   * Get routing rules for a specific Gmail account.
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account.
   * @returns Promise resolving to array of routing rule objects
   */
  async getRoutingRules(
    gmailAccountId?: string,
    limit?: number,
    offset?: number,
  ) {
    const params = new URLSearchParams();
    if (gmailAccountId) {
      params.append("gmail_account_id", gmailAccountId);
    }
    if (limit !== undefined) {
      params.append("limit", String(limit));
    }
    if (offset !== undefined) {
      params.append("offset", String(offset));
    }
    const queryString = params.toString();
    const url = queryString
      ? `/api/user/routing-rules?${queryString}`
      : "/api/user/routing-rules";
    return this.request<
      any[] | { entries: any[]; total: number; limit: number; offset: number }
    >(url);
  }

  /**
   * Add a new routing rule (automatically route matching emails to a folder).
   * @param pattern - Email pattern to match (supports wildcards like *@example.com)
   * @param folderId - Folder ID to route matching emails to
   * @param folderType - Type of folder: 'standard' or 'custom'
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account.
   * @returns Promise resolving to created routing rule
   */
  async addRoutingRule(
    pattern: string,
    folderId: string,
    folderType: "standard" | "custom",
    gmailAccountId?: string,
  ) {
    return this.request<any>("/api/user/routing-rules", {
      method: "POST",
      body: JSON.stringify({
        pattern,
        folder_id: folderId,
        folder_type: folderType,
        gmail_account_id: gmailAccountId,
      }),
    });
  }

  /**
   * Delete a routing rule.
   * @param entryId - Routing rule entry ID to delete
   * @param gmailAccountId - Gmail account ID that owns the rule
   * @returns Promise resolving to deletion result
   */
  async deleteRoutingRule(entryId: string, gmailAccountId: string) {
    return this.request<any>(
      `/api/user/routing-rules/${encodeURIComponent(gmailAccountId)}/${encodeURIComponent(entryId)}`,
      { method: "DELETE" },
    );
  }

  // Stats
  /**
   * Get user statistics (emails processed, flagged messages, etc.).
   * @param params - Optional parameters
   * @param params.windowHours - Time window in hours for statistics (default: all time)
   * @returns Promise resolving to user statistics object
   */
  async getUserStats(params?: {
    windowHours?: number;
    gmailAccountId?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (
      typeof params?.windowHours === "number" &&
      Number.isFinite(params.windowHours)
    ) {
      searchParams.set("window_hours", String(params.windowHours));
    }
    if (params?.gmailAccountId) {
      searchParams.set("gmail_account_id", params.gmailAccountId);
    }
    const query = searchParams.toString();
    const url = query ? `/api/user/stats?${query}` : "/api/user/stats";
    return this.request<any>(url);
  }

  // Messages
  /**
   * Get paginated message logs for a Gmail account.
   * @param limit - Number of messages to return (default: 50)
   * @param offset - Number of messages to skip for pagination (default: 0)
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account.
   * @param searchQuery - Optional search query to filter by sender email, domain, or reasons
   * @param options - Optional settings (folderFilter, includeSpamStatus)
   * @returns Promise resolving to paginated message logs with total count
   */
  async getMessages(
    limit: number = 50,
    offset: number = 0,
    gmailAccountId?: string,
    searchQuery?: string,
    options?: {
      folderFilter?: string;
      includeSpamStatus?: boolean;
    },
  ) {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    if (gmailAccountId) params.set("gmail_account_id", gmailAccountId);
    if (searchQuery && searchQuery.trim())
      params.set("search", searchQuery.trim());
    if (options?.folderFilter && options.folderFilter !== "all") {
      params.set("folder_filter", options.folderFilter);
    }
    if (options?.includeSpamStatus === false)
      params.set("include_spam_status", "false");
    return this.request<any>(`/api/user/messages?${params.toString()}`);
  }

  /**
   * Get detailed information about a specific message.
   * @param messageId - Gmail message ID to get details for
   * @param gmailAccountId - Optional Gmail account ID. If not provided, uses primary account.
   * @returns Promise resolving to detailed message object
   */
  async getMessageDetail(messageId: string, gmailAccountId?: string) {
    const params = new URLSearchParams();
    if (gmailAccountId) params.set("gmail_account_id", gmailAccountId);
    return this.request<any>(
      `/api/user/messages/${messageId}?${params.toString()}`,
    );
  }

  // Historical folder classification
  /**
   * Apply folder routing to historical messages (reprocess past emails).
   * Uses async background job processing to avoid timeouts.
   * @param days - Number of days to look back (30, 90, or 180)
   * @param _gmailEmail - Deprecated, kept for backward compatibility
   * @param gmailAccountId - Gmail account ID (required for async processing)
   * @returns Promise resolving to job info with processing status
   */
  async applyFoldersToHistoricalMessages(
    days: number = 30,
    _gmailEmail?: string,
    gmailAccountId?: string,
  ) {
    // Use new async job endpoint
    return this.request<{
      success: boolean;
      processing: boolean;
      message: string;
      jobId?: string;
      job?: any;
      processed?: number;
      labeled?: number;
    }>("/api/jobs/historical-classification", {
      method: "POST",
      body: JSON.stringify({
        gmailAccountId,
        days,
      }),
    });
  }

  /**
   * Get status of a historical classification job.
   * @param jobId - Job ID to check
   * @returns Promise resolving to job status
   */
  async getHistoricalClassificationJobStatus(jobId: string) {
    return this.request<{ job: any }>(
      `/api/jobs/historical-classification?jobId=${encodeURIComponent(jobId)}`,
    );
  }

  /**
   * Get active historical classification job for an account.
   * @param gmailAccountId - Gmail account ID
   * @returns Promise resolving to active job (if any)
   */
  async getActiveHistoricalClassificationJob(gmailAccountId: string) {
    return this.request<{ job: any | null }>(
      `/api/jobs/historical-classification?gmailAccountId=${encodeURIComponent(gmailAccountId)}`,
    );
  }

  /**
   * Cancel/clear a stuck historical classification job.
   * @param gmailAccountId - Gmail account ID
   * @returns Promise resolving to success message
   */
  async cancelHistoricalClassificationJob(gmailAccountId: string) {
    return this.request<{
      success: boolean;
      message: string;
      /**
       * Whether the Inngest cancel event was actually sent. When false, the
       * job record was cleared but the running job may still be processing
       * in the background.
       */
      cancelEventSent: boolean;
    }>(
      `/api/jobs/historical-classification?gmailAccountId=${encodeURIComponent(gmailAccountId)}`,
      {
        method: "DELETE",
      },
    );
  }

  // Billing
  /**
   * Create a Stripe checkout session for subscription.
   * @param plan - Subscription plan: 'monthly' (default) or 'yearly'
   * @returns Promise resolving to checkout URL
   */
  async createCheckout(plan: string = "monthly") {
    const successUrl = `${window.location.origin}/dashboard?checkout=success`;
    const cancelUrl = `${window.location.origin}/dashboard?checkout=canceled`;
    return this.request<{ checkout_url: string }>("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({
        plan,
        success_url: successUrl,
        cancel_url: cancelUrl,
      }),
    });
  }

  /**
   * Get payment methods for the current user.
   * @returns Promise resolving to array of payment methods with card details
   */
  async getPaymentMethods() {
    return this.request<{
      payment_methods: Array<{
        id: string;
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
        is_default: boolean;
      }>;
    }>("/api/billing/payment-methods");
  }

  /**
   * Update the default payment method for the current user.
   * @param paymentMethodId - Payment method ID from Setup Intent
   * @returns Promise resolving to success response
   */
  async updatePaymentMethod(paymentMethodId: string) {
    return this.request<{ success: boolean; payment_method_id: string }>(
      "/api/billing/payment-methods",
      {
        method: "POST",
        body: JSON.stringify({ payment_method_id: paymentMethodId }),
      },
    );
  }

  /**
   * Get current subscription status for the authenticated user.
   * @returns Promise resolving to subscription information (plan, status, current_period_end)
   */
  async getSubscription() {
    return this.request<{
      plan: string;
      status: string;
      current_period_end: string | null;
    }>("/api/billing/subscription");
  }

  // Referrals
  /**
   * Get referral summary including referral code, invite link, and statistics.
   * @returns Promise resolving to referral summary with code, link, reward info, and stats
   */
  async getReferralSummary() {
    return this.request<{
      referral_code: string;
      invite_link: string;
      reward: {
        months_free_per_conversion: number;
        note: string;
      };
      stats: {
        total: number;
        pending: number;
        qualified: number;
        rewarded: number;
        earned_months_free: number;
      };
    }>("/api/referrals/summary");
  }

  // Account deletion
  /**
   * Delete the current user's account (soft delete).
   * Cancels Stripe subscription if exists and marks account as deleted.
   * @returns Promise resolving to deletion result
   */
  async deleteAccount() {
    return this.request<any>("/api/user/delete", {
      method: "DELETE",
    });
  }

  // Debug: Rerun scoring for a message
  /**
   * Rerun scoring for a specific message (debug endpoint).
   * @param messageId - Gmail message ID to rerun scoring for
   * @returns Promise resolving to updated message with new score
   */
  async rerunScoring(messageId: string) {
    return this.request<any>("/api/debug/rerun-scoring", {
      method: "POST",
      body: JSON.stringify({ message_id: messageId }),
    });
  }

  /**
   * Manually route a message to a specific folder.
   * Used by the Chrome extension to allow users to manually categorize emails.
   * Applies the folder label and archives the message (except Important folder).
   * @param messageId - Gmail message ID to route
   * @param gmailAccountId - Gmail account ID that owns the message
   * @param folderId - Folder identifier (standard folder ID or custom folder ID)
   * @param folderType - Type of folder: 'standard' or 'custom'
   * @returns Promise resolving to route response with folder name
   */
  async routeMessageToFolder(
    messageId: string,
    gmailAccountId: string,
    folderId: string,
    folderType: "standard" | "custom",
  ) {
    return this.request<any>("/api/user/messages/route", {
      method: "POST",
      body: JSON.stringify({
        message_id: messageId,
        gmail_account_id: gmailAccountId,
        folder_id: folderId,
        folder_type: folderType,
      }),
    });
  }
}
